import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import './OfficeMyBookings.scss';

import { OfficeContext } from './Office';
import { getMyOfficeBookings, deleteOfficeBooking, updateOfficeBooking } from '../../api/office';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockOpenAlt, faLockAlt, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { SPACE_TYPE_LABELS, OFFICE_DOOR_STATES, DOOR_STATE_INFO, formatTimeClean, formatDayLabel } from '../../helpers/office_helpers';

const DOOR_ICONS = { open: faLockOpenAlt, closed: faLockAlt };

const durationLabel = (start, end) => {
  const mins = moment(end).diff(moment(start), 'minutes');
  if (mins >= 720) return 'Full Day';
  if (mins >= 360) return mins === 360 ? 'Half Day' : `${Math.round(mins / 60)} hr`;
  if (mins >= 60) return mins % 60 === 0 ? `${mins / 60} hr` : `${Math.round((mins / 60) * 10) / 10} hr`;
  return `${mins} min`;
};

const OfficeMyBookings = () => {
  const ctx = useContext(OfficeContext);
  const { onBookingChange } = ctx;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const resp = await getMyOfficeBookings();
      setBookings(resp?.bookings || []);
    } catch {
      window.ALERT.error('Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (booking, deleteMeeting) => {
    try {
      await deleteOfficeBooking(booking.id, deleteMeeting);
      window.ALERT.success('Booking cancelled');
      fetchBookings();
      onBookingChange();
    } catch (err) {
      window.ALERT.error(err || 'Failed to cancel booking.');
    }
  };

  const handleDoorChange = async (booking, newState) => {
    try {
      await updateOfficeBooking(booking.id, { door_state: newState });
      fetchBookings();
      onBookingChange();
    } catch (err) {
      window.ALERT.error(err || 'Failed to update door state.');
    }
  };

  if (loading) return <div className='office-my-bookings loading'>Loading...</div>;

  const grouped = _.groupBy(bookings, b => moment(b.starts_at).format('YYYY-MM-DD'));
  const sortedDates = Object.keys(grouped).sort();
  const today = moment().format('YYYY-MM-DD');

  return (
    <div className='office-my-bookings'>
      <div className='bookings-title'>Your Schedule</div>

      {sortedDates.length === 0 && (
        <div className='bookings-empty'>
          <div className='empty-text'>No upcoming bookings</div>
          <div className='empty-hint'>Head to the Office tab to reserve a space</div>
        </div>
      )}

      <div className='bookings-timeline'>
        {sortedDates.map(date => {
          const dayBookings = _.sortBy(grouped[date], 'starts_at');
          const isToday = date === today;
          const dayLabel = isToday ? 'Today' : formatDayLabel(date);
          const dateFormatted = moment(date).format('dddd, MMM D');

          return (
            <div key={date} className={cn('timeline-day', { today: isToday })}>
              <div className='day-header'>
                <div className='day-dot' />
                <div className='day-label'>
                  {isToday ? <span className='day-today'>Today</span> : null}
                  <span className='day-date'>{dateFormatted}</span>
                </div>
              </div>

              <div className='day-bookings'>
                {dayBookings.map(b => {
                  const typeLabel = SPACE_TYPE_LABELS[b.space?.type] || '';
                  const floorLabel = b.space?.floor?.name || '';
                  const hasMeeting = Boolean(b.google_calendar_event_id);
                  const dur = durationLabel(b.starts_at, b.ends_at);

                  return (
                    <div key={b.id} className='booking-card'>
                      <div className='booking-time-col'>
                        <div className='time-range'>
                          <span className='time-start'>{formatTimeClean(b.starts_at)}</span>
                          <span className='time-sep'>—</span>
                          <span className='time-end'>{formatTimeClean(b.ends_at)}</span>
                        </div>
                        <div className='time-duration'>{dur}</div>
                      </div>

                      <div className='booking-details'>
                        <div className='booking-name'>
                          {b.space?.name || 'Unknown'}
                          {b.space?.persistent_zoom_room_url && b.door_state !== OFFICE_DOOR_STATES.CLOSED && (
                            <a
                              href={b.space.persistent_zoom_room_url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='booking-enter-icon'
                              title={`Enter ${b.space.name}`}
                            >
                              <FontAwesomeIcon icon={faVideo} />
                            </a>
                          )}
                        </div>
                        <div className='booking-meta'>
                          {typeLabel && <span>{typeLabel}</span>}
                          {floorLabel && (
                            <>
                              <span className='meta-sep'>·</span>
                              <span>{floorLabel}</span>
                            </>
                          )}
                        </div>
                        {b.title && <div className='booking-meeting-title'>"{b.title}"</div>}
                      </div>

                      <div className='booking-actions'>
                        <div className='booking-action-btns'>
                          {b.zoom_join_url && (
                            <a href={b.zoom_join_url} target='_blank' rel='noopener noreferrer' className='zoom-link'>
                              <FontAwesomeIcon icon={faVideo} style={{ marginRight: '8px' }} />
                              Join Zoom
                            </a>
                          )}
                          <button className='cancel-btn' onClick={() => handleCancel(b)}>
                            {hasMeeting ? 'Cancel & Keep Meeting' : 'Cancel booking'}
                          </button>
                          {hasMeeting && (
                            <button className='cancel-btn' onClick={() => handleCancel(b, true)}>
                              Cancel &amp; Delete Meeting
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OfficeMyBookings;
